/**
 * Main Layout Component
 * Sidebar navigation + main content area with mobile support
 */

import { ReactNode, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { Icon, type IconName } from './icons';
import { clsx } from 'clsx';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  icon: IconName;
  label: string;
  badge?: string;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems: NavItem[] = [
    { to: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
    { to: '/brands', icon: 'brands', label: 'Brands', badge: 'New' },
    { to: '/create', icon: 'sparkles', label: 'Create Content' },
    { to: '/content-queue', icon: 'layers', label: 'Content Queue', badge: 'AI' },
    { to: '/brand-dna', icon: 'dna', label: 'Brand DNA' },
    { to: '/ad-builder', icon: 'megaphone', label: 'Ad Builder' },
    { to: '/email-campaigns', icon: 'mail', label: 'Email Campaigns' },
    { to: '/landing-pages', icon: 'globe', label: 'Landing Pages' },
    { to: '/posts', icon: 'edit', label: 'Posts' },
    { to: '/calendar', icon: 'calendar', label: 'Calendar' },
    { to: '/analytics', icon: 'bar-chart', label: 'Analytics' },
    { to: '/settings', icon: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        "w-64 bg-white border-r border-gray-200 flex flex-col fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-in-out",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo and Close Button */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/images/logo_only_k.png" alt="Jonkai Studio" className="w-8 h-8" />
              <h1 className="text-xl font-bold text-gray-900">Jonkai Studio</h1>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
            >
              <Icon name="x" size="md" />
            </button>
          </div>
          {selectedBrand && (
            <div className="mt-2 flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: selectedBrand.color_primary }}
              />
              <span className="text-sm text-gray-600">{selectedBrand.name}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <Icon name={item.icon} size="md" className="mr-3" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-medium">
                  {user?.name?.[0] || user?.email[0].toUpperCase()}
                </span>
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || user?.email}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="Logout"
            >
              <Icon name="logout" size="md" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile Header with Menu Button */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <Icon name="menu" size="lg" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/images/logo_only_k.png" alt="Jonkai Studio" className="w-6 h-6" />
            <h1 className="text-lg font-bold text-gray-900">Jonkai Studio</h1>
          </div>
          {selectedBrand && (
            <div className="ml-auto flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: selectedBrand.color_primary }}
              />
              <span className="text-sm text-gray-600 truncate max-w-[120px]">
                {selectedBrand.name}
              </span>
            </div>
          )}
        </div>

        <div className="max-w-7xl mx-auto p-4 sm:p-6">{children}</div>
      </main>
    </div>
  );
}
